/**
 * Workflow-logs API client.
 *
 * The workflow-logs routes (`/jobs`, `/logs`, `/job`, `/all`) are NOT served
 * on the main API host (`api-prod.zibby.app`). They live on a SEPARATE
 * RestApi — the `zibby-{stage}-workflow-subdomain` gateway — fronted by a
 * dedicated custom domain. The canonical host (matching how the backend's
 * MCP server reaches the same routes, see backend/src/handlers/mcp-server.js
 * WORKFLOWS_API_BASE) is:
 *
 *   prod        → https://logs.workflows.zibby.app
 *   dev/staging → https://logs.workflows-<stage>.zibby.app
 *   local       → same Express server as the main API (localhost:3001),
 *                 which mounts `app.get('/all/:projectId', ...)` directly.
 *
 * Base-URL resolution (no host is ever hard-coded as the sole source):
 *   1. REACT_APP_WORKFLOW_LOGS_BASE_URL — explicit override, wins if set.
 *   2. Otherwise DERIVE from REACT_APP_API_BASE_URL's hostname:
 *        api-prod.zibby.app      → https://logs.workflows.zibby.app
 *        api-staging.zibby.app   → https://logs.workflows-staging.zibby.app
 *        api-dev.zibby.app       → https://logs.workflows-dev.zibby.app
 *      This mirrors the webhook-URL derivation already in PipelineDetailPage's
 *      OverviewTab, so dev / staging / prod stay in lock-step automatically.
 *   3. Local / unknown hosts (localhost, 127.0.0.1, anything that isn't an
 *      `api-<stage>.zibby.app` shape) → fall back to the main API base. The
 *      Express dev server serves `/all` on the same origin, so local keeps
 *      working with no extra config.
 *
 * The endpoints require the same Bearer auth as the main API, so we reuse the
 * shared ApiClient (which injects the session token) pointed at the resolved
 * base.
 */

import { ApiClientFactory } from './api';

/**
 * Resolve the workflow-logs API base URL. Exported for unit testing /
 * sanity-checking the local-vs-prod wiring.
 */
export function resolveWorkflowLogsBaseUrl() {
  const explicit = process.env.REACT_APP_WORKFLOW_LOGS_BASE_URL;
  if (explicit) return explicit.replace(/\/$/, '');

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  let host = '';
  try {
    host = new URL(apiBase).hostname;
  } catch {
    host = '';
  }

  // api-<stage>.zibby.app → logs.workflows[-<stage>].zibby.app
  if (host === 'api-prod.zibby.app') {
    return 'https://logs.workflows.zibby.app';
  }
  const m = host.match(/^api-([a-z0-9]+)\.zibby\.app$/);
  if (m) {
    const stage = m[1];
    return stage === 'prod'
      ? 'https://logs.workflows.zibby.app'
      : `https://logs.workflows-${stage}.zibby.app`;
  }

  // Local dev (localhost / 127.0.0.1) and any unrecognised host: the Express
  // server mounts `/all` on the same origin as the main API, so reuse it.
  return apiBase.replace(/\/$/, '');
}

const workflowLogsApi = ApiClientFactory(resolveWorkflowLogsBaseUrl());

const WorkflowLogs = {
  /**
   * Fetch a page of a workflow's interleaved container logs.
   *
   * GET /all/{projectId}?workflow=<type>&limit=<n>&before=<epochMs>
   *
   * The backend (getAllWorkflowLogsHandler → getAllWorkflowLogs) reads from
   * the execution log group `/ecs/zibby-agent-<stage>`, which INCLUDES the
   * warm-runner "warming up" lines as well as the per-run execution output.
   *
   * Paging is a TIME-BOUNDED tail (not a forward CloudWatch cursor — that
   * walked the wrong direction, see BUG B): omit `before` for the NEWEST
   * window (initial load + live-tail), or pass `before=<oldest-loaded-ts>` to
   * fetch the page of OLDER events for scroll-up. Lines always come back
   * chronological (oldest→newest), so the newest sits at the BOTTOM.
   *
   * Response shape:
   *   {
   *     lines: Array<{ timestamp: number, message: string, jobId: string }>,
   *     hasOlder: boolean,        // more events exist EARLIER than lines[0]
   *     nextToken: null,          // legacy field, always null now
   *     jobCount: number,
   *     hasRunning?: boolean,     // true while any run is still executing
   *     message?: string,         // present when there are no streams yet
   *   }
   *
   * @param {string} projectId
   * @param {string} workflowType
   * @param {{ limit?: number, before?: number|null }} [opts]
   *        `before` = epoch ms; fetch events strictly earlier than this.
   */
  getWorkflowLogs(projectId, workflowType, { limit = 200, before } = {}) {
    if (!projectId) return Promise.reject(new Error('projectId is required'));
    if (!workflowType) return Promise.reject(new Error('workflowType is required'));
    const qs = new URLSearchParams({
      workflow: workflowType,
      limit: String(limit),
    });
    if (before != null) qs.set('before', String(before));
    return workflowLogsApi.get(`/all/${encodeURIComponent(projectId)}?${qs.toString()}`);
  },

  /**
   * Live, tenant-filtered tail of a Lambda-hosted chat agent's runtime logs.
   *
   * GET /chat-logs/{projectId}?workflow=<type>&since=<epochMs>&limit=<n>
   *
   * The chat runtime is a SHARED Lambda; its single CloudWatch log group
   * carries every tenant's lines. The backend (getChatRuntimeLogsHandler)
   * enforces tenant isolation server-side: verifyProjectAccess(userId,
   * projectId) + a literal `[tenant:<uuid>]` filterPattern the client cannot
   * widen. Lines come back chronological (oldest→newest), secret-scrubbed,
   * with the tenant token stripped.
   *
   * `since` (epoch ms) is the incremental-poll cursor — pass the previous
   * response's `nextSince` to fetch only strictly-newer events; omit it for
   * the newest bounded window (initial load).
   *
   * Response shape:
   *   {
   *     lines: Array<{ timestamp: number, message: string }>,
   *     nextSince: number,        // feed back as `since` on the next poll
   *     workflowUuid: string,     // the resolved tenant filter key
   *     filterScope: string,      // the literal filterPattern (no secrets)
   *     logGroup: string,
   *   }
   *
   * @param {string} projectId
   * @param {string} workflowType
   * @param {{ limit?: number, since?: number|null }} [opts]
   */
  getChatRuntimeLogs(projectId, workflowType, { limit = 300, since } = {}) {
    if (!projectId) return Promise.reject(new Error('projectId is required'));
    if (!workflowType) return Promise.reject(new Error('workflowType is required'));
    const qs = new URLSearchParams({
      workflow: workflowType,
      limit: String(limit),
    });
    if (since != null) qs.set('since', String(since));
    return workflowLogsApi.get(`/chat-logs/${encodeURIComponent(projectId)}?${qs.toString()}`);
  },
};

export default WorkflowLogs;
export { workflowLogsApi };
